import { useEffect, useRef } from 'react';
import type { Coordenada, PuntoViaje } from '../../lib/appTypes';

interface Props {
  coordenadas: Coordenada[];
  coordenadaActual?: Coordenada;
  coordenadaInicio?: Coordenada;
  coordenadaFin?: Coordenada;
  puntosIntermedios?: PuntoViaje[];
}

type LeafletLib = any;
type WinWithL = Window & { L?: LeafletLib };

function loadLeaflet(): Promise<LeafletLib> {
  return new Promise((resolve, reject) => {
    const win = window as unknown as WinWithL;
    if (win.L) {
      resolve(win.L);
      return;
    }
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => resolve((window as unknown as WinWithL).L);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function buildBounds(coordinates: Coordenada[]): [number, number][] {
  return coordinates.map(coordinate => [coordinate.lat, coordinate.lng] as [number, number]);
}

export default function MapaConductor({ coordenadas, coordenadaActual, coordenadaInicio, coordenadaFin, puntosIntermedios = [] }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const currentMarkerRef = useRef<any>(null);
  const startMarkerRef = useRef<any>(null);
  const destinationMarkerRef = useRef<any>(null);
  const tripPathRef = useRef<any>(null);
  const waypointMarkersRef = useRef<any[]>([]);

  useEffect(() => {
    let mounted = true;
    loadLeaflet().then(L => {
      if (!mounted || !mapRef.current || mapInstanceRef.current) return;

      const fallback: [number, number] = coordenadaFin
        ? [coordenadaFin.lat, coordenadaFin.lng]
        : coordenadaActual
        ? [coordenadaActual.lat, coordenadaActual.lng]
        : coordenadaInicio
        ? [coordenadaInicio.lat, coordenadaInicio.lng]
        : [-0.22985, -78.52495];

      const map = L.map(mapRef.current).setView(fallback, 14);
      mapInstanceRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);
    });

    return () => {
      mounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        currentMarkerRef.current = null;
        startMarkerRef.current = null;
        destinationMarkerRef.current = null;
        tripPathRef.current = null;
        waypointMarkersRef.current = [];
      }
    };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    loadLeaflet().then(L => {
      const currentIcon = L.divIcon({
        html: '<div style="font-size:26px;line-height:1">🚚</div>',
        className: '',
        iconAnchor: [13, 26],
      });
      const startIcon = L.divIcon({
        html: '<div style="font-size:24px;line-height:1">🟢</div>',
        className: '',
        iconAnchor: [12, 24],
      });
      const destinationIcon = L.divIcon({
        html: '<div style="font-size:24px;line-height:1">🎯</div>',
        className: '',
        iconAnchor: [12, 24],
      });

      if (coordenadaInicio) {
        if (startMarkerRef.current) {
          startMarkerRef.current.setLatLng([coordenadaInicio.lat, coordenadaInicio.lng]);
        } else {
          startMarkerRef.current = L.marker([coordenadaInicio.lat, coordenadaInicio.lng], { icon: startIcon })
            .addTo(map)
            .bindPopup('Punto de partida');
        }
      } else if (startMarkerRef.current) {
        map.removeLayer(startMarkerRef.current);
        startMarkerRef.current = null;
      }

      if (coordenadaFin) {
        if (destinationMarkerRef.current) {
          destinationMarkerRef.current.setLatLng([coordenadaFin.lat, coordenadaFin.lng]);
        } else {
          destinationMarkerRef.current = L.marker([coordenadaFin.lat, coordenadaFin.lng], { icon: destinationIcon })
            .addTo(map)
            .bindPopup('Destino');
        }
      } else if (destinationMarkerRef.current) {
        map.removeLayer(destinationMarkerRef.current);
        destinationMarkerRef.current = null;
      }

      waypointMarkersRef.current.forEach(marker => map.removeLayer(marker));
      waypointMarkersRef.current = [];

      const configuredPoints = puntosIntermedios
        .filter(point => typeof point.latitud === 'number' && typeof point.longitud === 'number')
        .map(point => ({ lat: point.latitud as number, lng: point.longitud as number }));

      configuredPoints.forEach((point, index) => {
        const waypointIcon = L.divIcon({
          html: `<div style="display:flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:9999px;background:#f97316;color:white;font-size:12px;font-weight:700;">${index + 1}</div>`,
          className: '',
          iconAnchor: [12, 12],
        });

        const marker = L.marker([point.lat, point.lng], { icon: waypointIcon })
          .addTo(map)
          .bindPopup(`Punto intermedio ${index + 1}`);

        waypointMarkersRef.current.push(marker);
      });

      if (coordenadaActual) {
        if (currentMarkerRef.current) {
          currentMarkerRef.current.setLatLng([coordenadaActual.lat, coordenadaActual.lng]);
        } else {
          currentMarkerRef.current = L.marker([coordenadaActual.lat, coordenadaActual.lng], { icon: currentIcon })
            .addTo(map)
            .bindPopup('Mi ubicación');
        }
      } else if (currentMarkerRef.current) {
        map.removeLayer(currentMarkerRef.current);
        currentMarkerRef.current = null;
      }

      const tripPoints = coordenadas.map(coordinate => [coordinate.lat, coordinate.lng] as [number, number]);
      if (tripPoints.length > 1) {
        if (tripPathRef.current) {
          tripPathRef.current.setLatLngs(tripPoints);
        } else {
          tripPathRef.current = L.polyline(tripPoints, {
            color: '#2563eb',
            weight: 3,
          }).addTo(map);
        }
      } else if (tripPathRef.current) {
        map.removeLayer(tripPathRef.current);
        tripPathRef.current = null;
      }

      const points = [
        ...coordenadas,
        ...([coordenadaInicio, coordenadaActual, coordenadaFin].filter(Boolean) as Coordenada[]),
        ...configuredPoints.map(point => ({ lat: point.lat, lng: point.lng, timestamp: 0 })),
      ];
      if (points.length >= 2) {
        map.fitBounds(buildBounds(points), { padding: [28, 28], maxZoom: 15 });
      } else if (points.length === 1) {
        const [point] = points;
        if (point) {
          map.setView([point.lat, point.lng], 15);
        }
      }
    });
  }, [coordenadaActual, coordenadaInicio, coordenadaFin, coordenadas, puntosIntermedios]);

  return (
    <div
      ref={mapRef}
      className="w-full rounded-xl overflow-hidden border border-gray-200"
      style={{ height: '320px' }}
    />
  );
}